import { inject } from '@angular/core';
import {
  UploadingStatus,
  DeviceConfigFilesResponseModel,
  DeviceConfigFileDetailsModel,
  ConfigurationFileFilterModel,
  ConfigFileUploading,
  InitialConfigFilesResponseModel,
  InitialConfigFileDetailsModel,
  InitialConfigFileFilterModel,
} from '@core/models';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, catchError, pipe, switchMap, tap } from 'rxjs';
import { SettingsService } from '@core/services/setting.service';
import { PAGINATION_DEFAULT } from '@core/constants';

type SettingsState = {
  isLoading: boolean;
  isDownloadingDeviceConfigFile: boolean;
  downloadDeviceConfigFileError: any;
  loadingConfigFilesError: any;
  deviceConfigFilesData: DeviceConfigFilesResponseModel | undefined;
  isLoadingDeviceConfigFiles: boolean;
  loadingDeviceFilesError: any;
  deviceConfigFileDetails: DeviceConfigFileDetailsModel | undefined;
  isLoadingDeviceConfigFileDetails: boolean;
  getDeviceConfigFileDetailsError: any;
  isDeletingDeviceConfigFile: boolean;
  deleteDeviceConfigFileError: any;
  ICFFilter: InitialConfigFileFilterModel;
  DCFFilter: ConfigurationFileFilterModel;
  configurationFileContentByVersionId: any;
  configurationFileUploadingStatus: UploadingStatus;
  isUploading: boolean;
  uploadError: string | null;
  initialConfigFilesData: InitialConfigFilesResponseModel | undefined;
  isLoadingInitialConfigFiles: boolean;
  initialConfigFileDetails: InitialConfigFileDetailsModel | undefined;
  isLoadingInitialConfigFileDetails: boolean;
  isDeletingInitialConfigFile: boolean;
};

const initialState: SettingsState = {
  isLoading: false,
  loadingConfigFilesError: null,
  deviceConfigFilesData: undefined,
  isLoadingDeviceConfigFiles: false,
  loadingDeviceFilesError: null,
  isDownloadingDeviceConfigFile: false,
  downloadDeviceConfigFileError: null,
  deviceConfigFileDetails: undefined,
  isLoadingDeviceConfigFileDetails: false,
  getDeviceConfigFileDetailsError: null,
  isDeletingDeviceConfigFile: false,
  deleteDeviceConfigFileError: null,
  ICFFilter: {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
    includeDeleted: false,
  },
  DCFFilter: {
    limit: PAGINATION_DEFAULT.limit,
    offset: PAGINATION_DEFAULT.offset,
    includeDeleted: false,
  },
  configurationFileContentByVersionId: undefined,
  configurationFileUploadingStatus: UploadingStatus.Loading,
  isUploading: false,
  uploadError: null,
  initialConfigFilesData: undefined,
  isLoadingInitialConfigFiles: false,
  initialConfigFileDetails: undefined,
  isLoadingInitialConfigFileDetails: false,
  isDeletingInitialConfigFile: false,
};

export const SettingsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store, settingsService = inject(SettingsService)) => ({
    resetUploadingStatusMethod: rxMethod<void>(
      pipe(
        tap(() => {
          patchState(store, {
            configurationFileUploadingStatus: UploadingStatus.Loading,
            isUploading: false,
          });
        }),
      ),
    ),

    uploadDCFCMethod: rxMethod<ConfigFileUploading>(
      pipe(
        switchMap(payload => {
          patchState(store, {
            configurationFileUploadingStatus: UploadingStatus.Loading,
            isUploading: true,
            uploadError: null,
          });
          return settingsService.uploadDCF(payload).pipe(
            catchError((e: any) => {
              patchState(store, {
                configurationFileUploadingStatus: UploadingStatus.Failed,
                isUploading: false,
                uploadError: e?.error?.message ?? e?.message ?? e?.detail,
              });
              return EMPTY;
            }),
            switchMap(() => {
              patchState(store, {
                configurationFileUploadingStatus: UploadingStatus.Successful,
                isUploading: false,
                isLoadingDeviceConfigFiles: true,
                deviceConfigFilesData: undefined,
              });
              return settingsService.getDeviceConfigFiles(store.DCFFilter());
            }),
            tapResponse({
              next: (data: DeviceConfigFilesResponseModel) => {
                patchState(store, {
                  deviceConfigFilesData: data,
                  isLoadingDeviceConfigFiles: false,
                });
              },
              error: () => {
                patchState(store, { isLoadingDeviceConfigFiles: false });
              },
            }),
          );
        }),
      ),
    ),

    uploadICFMethod: rxMethod<ConfigFileUploading>(
      pipe(
        switchMap(payload => {
          patchState(store, {
            configurationFileUploadingStatus: UploadingStatus.Loading,
            isUploading: true,
            uploadError: null,
          });
          return settingsService.uploadICF(payload).pipe(
            catchError((e: any) => {
              patchState(store, {
                configurationFileUploadingStatus: UploadingStatus.Failed,
                isUploading: false,
                uploadError: e?.error?.message ?? e?.message ?? e?.detail,
              });
              return EMPTY;
            }),
            switchMap(() => {
              patchState(store, {
                configurationFileUploadingStatus: UploadingStatus.Successful,
                isUploading: false,
                isLoadingInitialConfigFiles: true,
                initialConfigFilesData: undefined,
              });
              return settingsService.getInitialConfigFiles(store.ICFFilter());
            }),
            tapResponse({
              next: (data: InitialConfigFilesResponseModel) => {
                patchState(store, {
                  initialConfigFilesData: data,
                  isLoadingInitialConfigFiles: false,
                });
              },
              error: () => {
                patchState(store, { isLoadingInitialConfigFiles: false });
              },
            }),
          );
        }),
      ),
    ),

    getDeviceConfigFileDetails: rxMethod<number>(
      pipe(
        tap(() =>
          patchState(store, {
            deviceConfigFileDetails: undefined,
            isLoadingDeviceConfigFileDetails: true,
            getDeviceConfigFileDetailsError: null,
          }),
        ),
        switchMap(fileId => {
          return settingsService.getDeviceConfigFileDetails(fileId).pipe(
            tapResponse({
              next: (data: DeviceConfigFileDetailsModel) => {
                patchState(store, {
                  deviceConfigFileDetails: data,
                  isLoadingDeviceConfigFileDetails: false,
                });
              },
              error: e => {
                patchState(store, {
                  isLoadingDeviceConfigFileDetails: false,
                  getDeviceConfigFileDetailsError: e,
                });
              },
            }),
          );
        }),
      ),
    ),

    getInitialConfigFileDetails: rxMethod<number>(
      pipe(
        tap(() =>
          patchState(store, {
            initialConfigFileDetails: undefined,
            isLoadingInitialConfigFileDetails: true,
          }),
        ),
        switchMap(fileId => {
          return settingsService.getInitialConfigFileDetails(fileId).pipe(
            tapResponse({
              next: (data: InitialConfigFileDetailsModel) => {
                patchState(store, {
                  initialConfigFileDetails: data,
                  isLoadingInitialConfigFileDetails: false,
                });
              },
              error: () => {
                patchState(store, { isLoadingInitialConfigFileDetails: false });
              },
            }),
          );
        }),
      ),
    ),

    getDCFContentByVersionId: rxMethod<number>(
      pipe(
        switchMap(versionId => {
          return settingsService
            .getDeviceConfigFileContentByVersionId(
              store.deviceConfigFileDetails()!.fileId,
              versionId,
            )
            .pipe(
              tapResponse({
                next: (data: any) => {
                  patchState(store, {
                    configurationFileContentByVersionId: data,
                  });
                  const blob = new Blob([data], { type: 'text/json' });
                  const url = globalThis.URL.createObjectURL(blob);
                  globalThis.open(url);
                },
                error: () => {},
              }),
            );
        }),
      ),
    ),

    getICFContentByVersionId: rxMethod<number>(
      pipe(
        switchMap(versionId => {
          return settingsService
            .getInitialConfigFileContentByVersionId(
              store.initialConfigFileDetails()!.fileId,
              versionId,
            )
            .pipe(
              tapResponse({
                next: (data: any) => {
                  const blob = new Blob([data], { type: 'application/xml' });
                  const url = globalThis.URL.createObjectURL(blob);
                  globalThis.open(url);
                },
                error: () => {},
              }),
            );
        }),
      ),
    ),

    deleteDeviceConfigFile: rxMethod<number>(
      pipe(
        tap(() =>
          patchState(store, {
            deleteDeviceConfigFileError: null,
            isDeletingDeviceConfigFile: true,
          }),
        ),
        switchMap(fileId => {
          return settingsService.deleteDeviceConfigFile(fileId).pipe(
            tapResponse({
              next: () => {
                let currentDeviceConfigFilesData = store.deviceConfigFilesData();
                let files =
                  currentDeviceConfigFilesData?.files.filter(file => file.fileId !== fileId) ?? [];
                if (currentDeviceConfigFilesData) {
                  currentDeviceConfigFilesData.numberOfItems =
                    currentDeviceConfigFilesData.numberOfItems - 1;
                  currentDeviceConfigFilesData.files = files;
                }
                patchState(store, {
                  isDeletingDeviceConfigFile: false,
                  deviceConfigFilesData: currentDeviceConfigFilesData,
                });
              },
              error: e => {
                patchState(store, {
                  isDeletingDeviceConfigFile: false,
                  getDeviceConfigFileDetailsError: e,
                });
              },
            }),
          );
        }),
      ),
    ),

    deleteInitialConfigFile: rxMethod<number>(
      pipe(
        tap(() => patchState(store, { isDeletingInitialConfigFile: true })),
        switchMap(fileId => {
          return settingsService.deleteInitialConfigFile(fileId).pipe(
            tapResponse({
              next: () => {
                const current = store.initialConfigFilesData();
                const files = current?.files.filter(f => f.fileId !== fileId) ?? [];
                if (current) {
                  patchState(store, {
                    isDeletingInitialConfigFile: false,
                    initialConfigFilesData: {
                      ...current,
                      files,
                      numberOfItems: current.numberOfItems - 1,
                    },
                  });
                } else {
                  patchState(store, { isDeletingInitialConfigFile: false });
                }
              },
              error: () => {
                patchState(store, { isDeletingInitialConfigFile: false });
              },
            }),
          );
        }),
      ),
    ),

    getDeviceConfigFiles: rxMethod<ConfigurationFileFilterModel>(
      pipe(
        tap(() =>
          patchState(store, {
            deviceConfigFilesData: undefined,
            isLoadingDeviceConfigFiles: true,
            loadingDeviceFilesError: null,
          }),
        ),
        switchMap(filterPayload => {
          const filter: ConfigurationFileFilterModel = {
            ...store.DCFFilter(),
            ...filterPayload,
          };
          patchState(store, { DCFFilter: filter });
          return settingsService.getDeviceConfigFiles(filter).pipe(
            tapResponse({
              next: (data: DeviceConfigFilesResponseModel) => {
                patchState(store, {
                  deviceConfigFilesData: data,
                  isLoadingDeviceConfigFiles: false,
                  DCFFilter: filter,
                });
              },
              error: () => {
                patchState(store, {
                  isLoadingDeviceConfigFiles: false,
                  loadingDeviceFilesError: true,
                });
              },
            }),
          );
        }),
      ),
    ),

    getInitialConfigFiles: rxMethod<InitialConfigFileFilterModel>(
      pipe(
        tap(() =>
          patchState(store, {
            initialConfigFilesData: undefined,
            isLoadingInitialConfigFiles: true,
          }),
        ),
        switchMap(filterPayload => {
          const filter: InitialConfigFileFilterModel = {
            ...store.ICFFilter(),
            ...filterPayload,
          };
          patchState(store, { ICFFilter: filter });
          return settingsService.getInitialConfigFiles(filter).pipe(
            tapResponse({
              next: (data: InitialConfigFilesResponseModel) => {
                patchState(store, {
                  initialConfigFilesData: data,
                  isLoadingInitialConfigFiles: false,
                  ICFFilter: filter,
                });
              },
              error: () => {
                patchState(store, { isLoadingInitialConfigFiles: false });
              },
            }),
          );
        }),
      ),
    ),
  })),
);
